import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import API from '../../utils/api';
import { useConfirm } from '../../components/ConfirmModal';

const STATUS_COLOR = { open: 'danger', in_progress: 'warning', resolved: 'success', closed: 'secondary' };
const SUBJECT_EMOJI = { general: '💬', billing: '💳', technical: '🔧', account: '👤', feature: '✨', voice: '🎙️', feedback: '⭐', other: '📩' };

const AdminSupport = () => {
  const confirm = useConfirm();
  const [searchParams, setSearchParams] = useSearchParams();
  const [messages, setMessages] = useState([]);
  const [total, setTotal]       = useState(0);
  const [pages, setPages]       = useState(1);
  const [statusCounts, setStatusCounts] = useState({ open: 0, in_progress: 0, resolved: 0, closed: 0 });
  const [loading, setLoading]   = useState(true);
  const [selected, setSelected] = useState(null); // open message in detail panel
  const [saving, setSaving]     = useState(false);
  const [noteText, setNoteText] = useState('');

  const status  = searchParams.get('status')  || '';
  const subject = searchParams.get('subject') || '';
  const search  = searchParams.get('search')  || '';
  const page    = parseInt(searchParams.get('page') || '1');
  const [searchInput, setSearchInput] = useState(search);

  const load = useCallback(() => {
    setLoading(true);
    const params = { page, limit: 15 };
    if (status)  params.status  = status;
    if (subject) params.subject = subject;
    if (search)  params.search  = search;
    API.get('/admin/support', { params })
      .then(r => {
        setMessages(r.data.messages);
        setTotal(r.data.total);
        setPages(r.data.pages);
        setStatusCounts(r.data.statusCounts || {});
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [status, subject, search, page]);

  useEffect(() => { load(); }, [load]);

  // Reload selected message after save
  useEffect(() => {
    if (selected) {
      const fresh = messages.find(m => m._id === selected._id);
      if (fresh) setSelected(fresh);
    }
  }, [messages]); // eslint-disable-next-line -- selected intentionally omitted to avoid infinite loop

  const set = (key, val) => {
    const p = Object.fromEntries(searchParams);
    if (val) p[key] = val; else delete p[key];
    if (key !== 'page') p.page = '1';
    setSearchParams(p);
  };

  const openMessage = (m) => {
    setSelected(m);
    setNoteText(m.adminNotes || '');
  };

  const saveMessage = async (update) => {
    setSaving(true);
    try {
      const r = await API.patch(`/admin/support/${selected._id}`, update);
      setSelected(r.data.message);
      load();
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const deleteMessage = async (id) => {
    const ok = await confirm({ title: 'Delete message?', message: 'This support message will be permanently deleted.', confirmLabel: 'Delete', danger: true });
    if (!ok) return;
    await API.delete(`/admin/support/${id}`);
    if (selected?._id === id) setSelected(null);
    load();
  };

  return (
    <AdminLayout title="Support">
      {/* Status tab pills */}
      <div className="d-flex gap-2 flex-wrap mb-4">
        {[
          { val: '',            label: 'All',         count: total },
          { val: 'open',        label: 'Open',        count: statusCounts.open },
          { val: 'in_progress', label: 'In Progress', count: statusCounts.in_progress },
          { val: 'resolved',    label: 'Resolved',    count: statusCounts.resolved },
          { val: 'closed',      label: 'Closed',      count: statusCounts.closed },
        ].map(tab => (
          <button
            key={tab.val}
            onClick={() => set('status', tab.val)}
            className={`btn btn-sm rounded-pill px-3 ${status === tab.val ? 'btn-dream' : 'btn-outline-secondary'}`}
            style={{ fontSize: '13px' }}>
            {tab.label} {tab.count > 0 && <span className="badge rounded-pill ms-1" style={{ background: status === tab.val ? 'rgba(255,255,255,0.3)' : 'var(--header-bg)', color: status === tab.val ? '#fff' : 'var(--text-body)', fontSize: '10px' }}>{tab.count}</span>}
          </button>
        ))}

        {/* Search */}
        <form className="ms-auto d-flex gap-2" onSubmit={e => { e.preventDefault(); set('search', searchInput); }}>
          <input className="form-control form-control-sm" placeholder="Search…" value={searchInput}
            onChange={e => setSearchInput(e.target.value)} style={{ borderRadius: '8px', width: '180px' }} />
          <button type="submit" className="btn btn-sm btn-outline-secondary rounded-pill">
            <i className="bi bi-search"></i>
          </button>
        </form>
      </div>

      <div className="row g-3" style={{ minHeight: '500px' }}>
        {/* Message list */}
        <div className={selected ? 'col-lg-5' : 'col-12'}>
          {loading ? (
            <div className="d-flex justify-content-center py-5">
              <div className="spinner-border" style={{ color: '#6f42c1' }}></div>
            </div>
          ) : messages.length === 0 ? (
            <div className="card border-0 shadow-sm text-center p-5" style={{ borderRadius: '16px' }}>
              <div style={{ fontSize: '48px' }}>✉️</div>
              <p className="text-muted mt-2">No messages found</p>
            </div>
          ) : (
            <div className="d-flex flex-column gap-2">
              {messages.map(m => (
                <div
                  key={m._id}
                  onClick={() => openMessage(m)}
                  className="card border-0 shadow-sm"
                  style={{
                    borderRadius: '12px',
                    cursor: 'pointer',
                    borderLeft: `3px solid var(--bs-${STATUS_COLOR[m.status]})`,
                    background: selected?._id === m._id ? '#f8f5ff' : '#fff',
                  }}>
                  <div className="card-body p-3">
                    <div className="d-flex justify-content-between align-items-start mb-1">
                      <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-main)' }}>
                        {SUBJECT_EMOJI[m.subject]} {m.name}
                        {m.userId && <i className="bi bi-person-check ms-1 text-success" title="Linked account" style={{ fontSize: '12px' }}></i>}
                        {m.attachments?.length > 0 && (
                          <span className="ms-1 badge rounded-pill"
                            style={{ background: 'var(--purple-section)', color: '#6f42c1', fontSize: '10px', fontWeight: 600 }}
                            title={`${m.attachments.length} screenshot${m.attachments.length > 1 ? 's' : ''}`}>
                            <i className="bi bi-image me-1" style={{ fontSize: '9px' }}></i>{m.attachments.length}
                          </span>
                        )}
                      </div>
                      <span className={`badge bg-${STATUS_COLOR[m.status]} bg-opacity-10 text-${STATUS_COLOR[m.status]}`} style={{ fontSize: '10px', whiteSpace: 'nowrap' }}>
                        {m.status.replace('_', ' ')}
                      </span>
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted2)' }}>{m.email}</div>
                    <div style={{ fontSize: '13px', color: 'var(--text-body)', marginTop: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {m.message}
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--text-soft)', marginTop: '4px' }}>
                      {new Date(m.createdAt).toLocaleString()}
                    </div>
                  </div>
                </div>
              ))}

              {/* Pagination */}
              {pages > 1 && (
                <div className="d-flex justify-content-center gap-2 mt-2">
                  <button className="btn btn-sm btn-outline-secondary rounded-pill" disabled={page <= 1}
                    onClick={() => set('page', String(page - 1))}>← Prev</button>
                  <span className="btn btn-sm disabled">{page} / {pages}</span>
                  <button className="btn btn-sm btn-outline-secondary rounded-pill" disabled={page >= pages}
                    onClick={() => set('page', String(page + 1))}>Next →</button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Detail panel */}
        {selected && (
          <div className="col-lg-7">
            <div className="card border-0 shadow-sm" style={{ borderRadius: '16px', position: 'sticky', top: '72px' }}>
              <div className="card-body p-4">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div>
                    <h6 className="fw-bold mb-0" style={{ color: 'var(--text-main)' }}>
                      {SUBJECT_EMOJI[selected.subject]} {selected.name}
                    </h6>
                    <div style={{ fontSize: '13px', color: 'var(--text-muted2)' }}>{selected.email}</div>
                    {selected.userId && (
                      <div style={{ fontSize: '12px', color: '#059669', marginTop: '2px' }}>
                        <i className="bi bi-person-check me-1"></i>
                        Linked: {selected.userId.name} ({selected.userId.subscription?.plan})
                      </div>
                    )}
                  </div>
                  <button onClick={() => setSelected(null)} className="btn btn-sm btn-outline-secondary rounded-pill px-2" style={{ fontSize: '12px' }}>
                    <i className="bi bi-x"></i>
                  </button>
                </div>

                {/* Message */}
                <div className="p-3 rounded-3 mb-3" style={{ background: 'var(--page-bg)', fontSize: '14px', color: 'var(--text-body)', lineHeight: 1.7, maxHeight: '200px', overflowY: 'auto', whiteSpace: 'pre-wrap' }}>
                  {selected.message}
                </div>

                {/* Attachments */}
                {selected.attachments?.length > 0 && (
                  <div className="mb-3">
                    <div className="fw-semibold mb-2" style={{ fontSize: '13px', color: 'var(--text-main)' }}>
                      Attachments ({selected.attachments.length})
                    </div>
                    <div className="d-flex gap-2 flex-wrap">
                      {selected.attachments.map((a, i) => (
                        <a key={i} href={a.url} target="_blank" rel="noopener noreferrer"
                          title="Click to open full size">
                          <img
                            src={a.url}
                            alt={`Attachment ${i + 1}`}
                            style={{
                              width: '96px', height: '96px', objectFit: 'cover',
                              borderRadius: '10px', border: '2px solid #e0d4f7',
                              display: 'block', cursor: 'pointer',
                              transition: 'transform 0.15s',
                            }}
                            onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.04)'}
                            onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                          />
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {/* Status changer */}
                <div className="mb-3">
                  <div className="fw-semibold mb-2" style={{ fontSize: '13px', color: 'var(--text-main)' }}>Update Status</div>
                  <div className="d-flex gap-2 flex-wrap">
                    {['open', 'in_progress', 'resolved', 'closed'].map(s => (
                      <button
                        key={s}
                        disabled={saving || selected.status === s}
                        onClick={() => saveMessage({ status: s })}
                        className={`btn btn-sm rounded-pill px-3 ${selected.status === s ? `btn-${STATUS_COLOR[s]}` : `btn-outline-${STATUS_COLOR[s]}`}`}
                        style={{ fontSize: '12px' }}>
                        {s.replace('_', ' ')}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Admin notes */}
                <div className="mb-3">
                  <div className="fw-semibold mb-2" style={{ fontSize: '13px', color: 'var(--text-main)' }}>Internal Notes</div>
                  <textarea
                    className="form-control form-control-sm"
                    rows={3}
                    placeholder="Add internal notes (not visible to user)…"
                    value={noteText}
                    onChange={e => setNoteText(e.target.value)}
                    style={{ borderRadius: '10px', resize: 'none', fontSize: '13px' }}
                  />
                  <button
                    disabled={saving}
                    onClick={() => saveMessage({ adminNotes: noteText })}
                    className="btn btn-sm btn-dream rounded-pill mt-2 px-4"
                    style={{ fontSize: '12px' }}>
                    Save Notes
                  </button>
                </div>

                {/* Actions */}
                <div className="d-flex gap-2">
                  <a href={`mailto:${selected.email}?subject=Re: ${selected.subject}`}
                    className="btn btn-sm btn-outline-secondary rounded-pill flex-fill text-center" style={{ fontSize: '12px' }}>
                    <i className="bi bi-reply me-1"></i>Reply via Email
                  </a>
                  <button
                    onClick={() => deleteMessage(selected._id)}
                    className="btn btn-sm btn-outline-danger rounded-pill px-3" style={{ fontSize: '12px' }}>
                    <i className="bi bi-trash"></i>
                  </button>
                </div>

                <div style={{ fontSize: '11px', color: 'var(--text-soft)', marginTop: '12px' }}>
                  Received {new Date(selected.createdAt).toLocaleString()}
                  {selected.updatedAt && selected.updatedAt !== selected.createdAt &&
                    ` · Updated ${new Date(selected.updatedAt).toLocaleString()}`}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminSupport;
